import React, { useState, useRef, useEffect } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/system';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import {
  Button,
  Card,
  CardMedia,
  Divider,
  Grid,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';

// Define types for props
interface CardData {
  url: string;
  image: string;
  date: string;
  title: string;
  description: string;
}

interface NDCNewsProps {
  data: CardData[];
  // onContentUpdate?: () => void;
}

const NDCNews: React.FC<NDCNewsProps> = ({ data }) => {
  const [showAll, setShowAll] = useState(false);
  const theme = useTheme();
  const { t, i18n } = useTranslation();

  const isDesktop = useMediaQuery(theme.breakpoints.up(1200));
  const isTablet = useMediaQuery('(min-width:768px) and (max-width:1200px)');
  const isMobile = useMediaQuery(theme.breakpoints.down(767));

  const cardRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const topSectionRef = useRef<HTMLDivElement | null>(null); // Ref for the top of the section

  const initialCards = isDesktop ? 3 : 2;

  const cardsToShow = showAll ? data : data.slice(0, initialCards);

  const formatDate = (dateString: string): string => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    return new Date(dateString).toLocaleDateString(i18n.language, options);
  };

  // useEffect(() => {
  //   if (onContentUpdate) onContentUpdate();
  // }, [showAll, onContentUpdate]);

  const handleViewMoreClick = (): void => {
    setShowAll((prev) => !prev);

    if (showAll) {
      // Scroll to the top of the news section when "View Less" is clicked
      setTimeout(() => {
        if (topSectionRef.current) {
          const topPosition =
            topSectionRef.current.getBoundingClientRect().top + window.scrollY;
          window.scrollTo({
            top: topPosition - 200, // Adjusted offset to move further up
            behavior: 'smooth'
          });
          topSectionRef.current.focus(); // Set focus to the top section
        }
      }, 0);
    }
  };

  useEffect(() => {
    if (showAll) {
      const focusIndex = isDesktop ? 3 : 2; // 4th card for desktop, 3rd for tablet/mobile
      setTimeout(() => {
        if (cardRefs.current[focusIndex]) {
          cardRefs.current[focusIndex]?.focus();
        }
      }, 0);
    }
  }, [showAll, isDesktop]);

  return (
    <Box
      ref={topSectionRef} // Attach the ref to the top of the section
      // tabIndex={-1} // Make it focusable
      sx={{
        width: '100%',
        // justifySelf:"center",
        padding: isMobile ? 8 : isTablet ? 10 : 10,
        pt: 2,
        pb: 6
      }}
    >
      <Grid container spacing={4} role="list" alignItems="stretch">
        {cardsToShow.map((card, index) => {
          const truncateDescription = (description: string, charLimit: number): string => {
            return description.length > charLimit
              ? `${description.slice(0, charLimit)}...`
              : description;
          };
         
          const truncatedDescription = truncateDescription(card.description, 80);
          return(
          <Grid
            item
            xs={12}
            sm={6}
            md={isTablet ? 6 : 4}
            key={index}
            role="listitem"
            aria-posinset={index + 1}
            aria-setsize={cardsToShow.length}
            aria-hidden={!showAll && index >= initialCards} // Hide cards beyond the initial limit
          >
            <Box
              // tabIndex={0}
              ref={(el: any) => (cardRefs.current[index] = el)}
              sx={{
                display: 'block',
                height: '100%',
                textDecoration: 'none',
                justifyItems:"center"
              }}
            >
              <Card
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  width: (isMobile || isTablet) ? '336px' : '380px', // Fixed width for cards
                  height: '100%', // Stretch to match the tallest card
                  padding: 2,
                  backgroundColor: '#FFFFFF',
                  borderRadius: '16px',
                  transition:
                    'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                  boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    boxShadow: '0px 8px 12px rgba(0, 0, 0, 0.2)',
                    '& a': {
                      color: '#003D78' // Change "Read More" color on card hover
                    }
                  }
                }}
                role="article"
                aria-labelledby={`news-title-${index}`}
                aria-describedby={`news-desc-${index}`}
              >
                {/* Group 1: Image, Date, and Title */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <CardMedia
                    component="img"
                    sx={{
                      objectFit: 'cover',
                      width: '113%',
                      ml: '-16px',
                      mt: '-16px',
                      mr: '-30px'
                    }}
                    src={card.image}
                    alt="NDC News Image"
                  />
                  <Typography
                    id={`news-title-${index}`}
                    component="h3"
                    sx={{
                      fontFamily: 'Open Sans',
                      fontWeight: '700 !important',
                      fontSize: '18px',
                      lineHeight: '28px',
                      px: '10px',
                      mt: 1 // Add spacing below the date
                    }}
                    dangerouslySetInnerHTML={{ __html: card.title }}
                  />
                  <Typography
                    sx={{
                      fontFamily: 'Open Sans',
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '20px',
                      px: '10px',
                      m: '1rem 0' // Add spacing below the image
                    }}
                    aria-label={`Published on ${formatDate(card.date)}`}
                  >
                    {formatDate(card.date)}
                  </Typography>

                  <Typography
                    id={`news-desc-${index}`}
                    variant="body1"
                    sx={{
                      fontFamily: 'Open Sans',
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '20px',
                      letterSpacing: 0,
                      px: '10px'
                    }}
                    dangerouslySetInnerHTML={{ __html: truncatedDescription }}
                  />
                </Box>

                {/* Group 2: Description, Divider, and Read More */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <Divider
                    sx={{ mt: 2, mb: 3, color: '#C9CACC', height: '1px' }}
                    aria-hidden="true"
                  />
                  <Typography
                    variant="body2"
                    component="a"
                    href={card.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    role="button"
                    aria-label={`Read more about ${card.title}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      fontFamily: 'Open Sans',
                      fontWeight: 700,
                      fontSize: '16px',
                      lineHeight: '20px',
                      letterSpacing: '0px',
                      color: '#1460AA',
                      px: '10px',
                      pb: '5px',
                      textDecoration: 'none'
                    }}
                  >
                    {t('ndc_news_var.readMore')}
                    <NavigateNextIcon
                      sx={{
                        mb: '-5px',
                        top: '1.33px',
                        ml: '14px'
                      }}
                    />
                  </Typography>
                </Box>
              </Card>
            </Box>
          </Grid>
        )
})}
      </Grid>
      <Box
        mt={isMobile || isTablet ? 4 : 6}
        display="flex"
        justifyContent="center"
      >
        <Button
          role="button"
          aria-label={
            showAll
              ? t('ndc_news_var.viewLessText')
              : t('ndc_news_var.moreButton')
          }
          sx={{
            width: isMobile ? '100%' : 'auto',
            borderRadius: '24px',
            backgroundColor: '#1460AA',
            height: '44px',
            px: 4,
            fontFamily: 'Open Sans',
            fontWeight: 700,
            fontSize: '16px',
            lineHeight: '20px',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: '#003D78'
            }
          }}
          variant="contained"
          onClick={handleViewMoreClick}
          endIcon={showAll ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        >
          {showAll
            ? t('ndc_news_var.lessButton')
            : t('ndc_news_var.moreButton')}
        </Button>
      </Box>
    </Box>
  );
};

export default NDCNews;
